import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { termsAndConditionDefault } from "constant";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  query,
  collection,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { firestore, uploadFile } from "firebaseConfig";
import moment from "moment";
import { referralCodeGenerator } from "utils/common";
import { sendWelcomeEmailToAdmin } from "firebaseConfig";
import { toast } from "react-toastify";
import { get_hsn_sac } from "firebaseConfig";
import posthog from "posthog-js";
import { enablePostHogErrorTracking } from "utils/posthogErrorTracking";
import { enableUserAccount } from "firebaseConfig";
import { disableUserAccount } from "firebaseConfig";

const normalizeEmail = (email) => email?.trim().toLowerCase();

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async ({ auth, email, password }, thunkAPI) => {
    try {
      let status, data, message;
      let profileData;
      let adminProfileData;
      const normalizedEmail = normalizeEmail(email);

      try {
        const res = await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );
        data = { ...res?.user };
        status = 200;

        let q = query(
          collection(firestore, "employees"),
          where("email", "==", normalizeEmail(data.email))
        );
        let querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          let employeeDoc = querySnapshot.docs[0].data();
          const adminId = employeeDoc.adminId;

          const adminProfileRef = doc(firestore, "profile", adminId);
          const adminSnapshot = await getDoc(adminProfileRef);

          if (!adminSnapshot.exists()) {
            message = "Admin profile not found";
            return thunkAPI.rejectWithValue({ message });
          }

          adminProfileData = adminSnapshot.data();
          const subscription = adminProfileData.activeSubscription;
          if (subscription) {
            const startDate = moment(subscription.startDate.toDate());
            const expiryDate = startDate
              .clone()
              .add(subscription.monthOfSubscription, "months")
              .add(subscription.daysLeftFromLastSubscription || 0, "days");

            const today = moment();
            if (today.isAfter(expiryDate, "day")) {
              message =
                "Your subscription has expired. Please contact your admin.";
              return thunkAPI.rejectWithValue({
                message,
                uid: adminId,
                isSuccess: false,
                isError: true,
                isEmployee: true,
                ...employeeDoc,
              });
            }
          }

          const hsnCode = await get_hsn_sac();
          const unitsRef = collection(firestore, "units");
          const unitsSnapshot = await getDocs(unitsRef);
          const unitsList = unitsSnapshot.docs.map((doc) => ({
            ...doc.data(),
          }));

          const gstTreatmentsRef = collection(firestore, "gstTreatments");
          const gstTreatmentsSnapshot = await getDocs(gstTreatmentsRef);
          const gstTreatmentsList = gstTreatmentsSnapshot.docs.map((doc) => ({
            ...doc.data(),
          }));

          data = {
            ...employeeDoc,
            uid: adminId,
            accessModules: employeeDoc.accessModules,
            hsnCode: { ...hsnCode },
            unitList: unitsList[0]?.data,
            gstTreatmentsList: gstTreatmentsList[0]?.data,
            isEmployee: true,
          };
        } else {
          const roleRef = doc(firestore, "profile", data.uid);
          const snapshot = await getDoc(roleRef);

          if (!snapshot.exists()) {
            message = "User profile not found";
            return thunkAPI.rejectWithValue({ message });
          }

          profileData = snapshot.data();

          const subscription = profileData.activeSubscription;
          if (subscription) {
            const startDate = moment(subscription.startDate.toDate());
            const expiryDate = startDate
              .clone()
              .add(subscription.monthOfSubscription, "months")
              .add(subscription.daysLeftFromLastSubscription || 0, "days");

            const today = moment();
            if (today.isAfter(expiryDate, "day")) {
              message =
                "Your subscription has expired. Please renew to continue.";
              return thunkAPI.rejectWithValue({
                message,
                uid: data.uid,
                isSuccess: false,
                isError: true,
                redirectToPayment: true,
                ...profileData,
              });
            }
          }

          const hsnCode = await get_hsn_sac();
          const unitsRef = collection(firestore, "units");
          const unitsSnapshot = await getDocs(unitsRef);
          const unitsList = unitsSnapshot.docs.map((doc) => ({
            ...doc.data(),
          }));

          const gstTreatmentsRef = collection(firestore, "gstTreatments");
          const gstTreatmentsSnapshot = await getDocs(gstTreatmentsRef);
          const gstTreatmentsList = gstTreatmentsSnapshot.docs.map((doc) => ({
            ...doc.data(),
          }));

          data = {
            ...data,
            ...profileData,
            hsnCode: { ...hsnCode },
            unitList: unitsList[0]?.data,
            gstTreatmentsList: gstTreatmentsList[0]?.data,
          };
        }
      } catch (error) {
        console.log("error", error);
        message = `${normalizedEmail} credentials are incorrect, please retry`;
        if (error.code === "resource-exhausted") {
          message = "Server error";
        }
        status = 400;
      }

      if (status === 200) {
        localStorage.setItem("token", data?.accessToken);

        let mainAdminProfile = adminProfileData || profileData;

        enablePostHogErrorTracking({
          name: mainAdminProfile.name || "",
          email: mainAdminProfile.email?.toLowerCase(),
          uid: mainAdminProfile.uid,
        });

        return { ...data, email: normalizedEmail };
      } else {
        return thunkAPI.rejectWithValue({ ...data, message });
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const registerUser = createAsyncThunk(
  "user/registerUser",
  async (
    { formdata, payResponse, referredBy, paymentData, isActivation },
    thunkAPI
  ) => {
    try {
      const auth = getAuth();
      const state = thunkAPI.getState();
      let userId = state.user.createdUserId;

      if (!isActivation) {
        const profilesRef = collection(firestore, "profile");
        const q = query(
          profilesRef,
          where("email", "==", formdata.email.toLowerCase())
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const existingDoc = querySnapshot.docs[0];
          const profileData = existingDoc.data();

          // 🟡 If the payment is still pending, allow user to continue payment
          const pendingPayment = profileData?.payment?.some(
            (p) => p.paymentStatus === "pending"
          );

          if (pendingPayment) {
            return {
              success: true,
              userId: existingDoc.id,
              message: "User already exists with pending payment",
              pendingUserData: profileData,
            };
          } else {
            // ❌ If already paid and active
            throw new Error(
              "User with this email already exists and is active"
            );
          }
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formdata.email,
          formdata.password
        );
        const user = userCredential.user;
        userId = user.uid;

        const commonProfile = {
          name: formdata.name,
          email: formdata.email,
          phoneNumber: formdata.phoneNumber,
          countryCode: formdata.countryCode?.value || "+91",
          code: referralCodeGenerator(formdata.name),
          gstNumber: formdata.gstNumber || null,
          firmName: formdata.firmName || null,
          referenceCode: formdata.referenceCode || null,
          createdAt: new Date(),
          // 🟡 Create activeSubscription but with null startDate
          activeSubscription: {
            plan: paymentData?.plan || null,
            startDate: null, // <-- initially null
            monthOfSubscription: paymentData?.months || null,
            daysLeftFromLastSubscription: 0,
          },
          // 🧾 Store base payment details in pending state
          payment: [
            {
              plan: paymentData?.plan || null,
              months: paymentData?.months || null,
              baseAmount: paymentData?.paymentAmount || null,
              referralAmount: paymentData?.referralAmount || 0,
              gstPercentage: paymentData?.gstPercentage || 18,
              totalPayable: paymentData?.totalAmount || 0,
              paymentStatus: "pending",
              createdAt: new Date(),
            },
          ],
        };

        const profile = referredBy
          ? { ...commonProfile, referredBy }
          : commonProfile;

        await setDoc(doc(firestore, "profile", userId), profile);
        await disableUserAccount({ uid: userId });

        return {
          success: true,
          userId,
          message: "Almost there! Complete the payment to activate your account.",
        };
      }

      // 🧩 2️⃣ ACTIVATE USER (After Successful Payment)
      if (isActivation && userId) {
        await enableUserAccount({ uid: userId });

        const paymentInfo = {
          plan: paymentData?.plan,
          months: paymentData?.months,
          gstPercentage: paymentData?.gstPercentage,
          referralAmount: paymentData?.referralAmount || 0,
          subscriptionAmount: payResponse?.subscriptionAmount || 0,
          gstAmount: payResponse?.gstAmount || 0,
          totalAmount: payResponse?.totalAmount || 0,
          finalAmountPaid: payResponse?.amount || 0,
          razorpay_payment_id: payResponse?.razorpay_payment_id || null,
          razorpay_order_id: payResponse?.razorpay_order_id || null,
          razorpay_signature: payResponse?.razorpay_signature || null,
          paymentStatus: "success",
          createdAt: new Date(),
        };

        const commonProfile = {
          payment: [paymentInfo],
          // 🟢 Now we activate the subscription
          activeSubscription: {
            plan: paymentData?.plan,
            startDate: new Date(), // <-- now set when payment succeeds
            monthOfSubscription: paymentData?.months,
            daysLeftFromLastSubscription: 0,
          },
          updatedAt: new Date(),
        };

        const profile = referredBy
          ? { ...commonProfile, referredBy }
          : commonProfile;

        await updateDoc(doc(firestore, "profile", userId), profile);

        // 📨 Send admin notification
        const currentDomain = window.location.origin;
        await sendWelcomeEmailToAdmin({
          adminName: formdata.name,
          userEmail: formdata.email,
          url: currentDomain,
        }).catch((err) => console.log("Mail sent error:", err));

        return {
          success: true,
          userId,
          message: "User activated successfully",
        };
      }

      return { success: false, message: "Invalid registration stage" };
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const renewSubscription = createAsyncThunk(
  "user/renewSubscription",
  async (
    { payResponse, uid, referralList, subscription, paymentData },
    thunkAPI
  ) => {
    try {
      const ref = doc(firestore, "profile", uid);
      const snapshot = await getDoc(ref);
      const data = { ...snapshot.data() };

      const today = moment();
      const otherDate = moment(data.activeSubscription.startDate.toDate())
        .add(data.activeSubscription.monthOfSubscription, "months")
        .add(data.activeSubscription.daysLeftFromLastSubscription, "days");

      const isAfter = today.isAfter(otherDate, "day");

      let activeSubscription_;
      if (isAfter) {
        activeSubscription_ = {
          plan: subscription.plan,
          startDate: new Date(),
          monthOfSubscription: subscription.months,
          daysLeftFromLastSubscription: 0,
        };
      } else {
        const diffInDays = otherDate.diff(today, "days");
        activeSubscription_ = {
          plan: subscription.plan,
          startDate: new Date(),
          monthOfSubscription: subscription.months,
          daysLeftFromLastSubscription: diffInDays + 1,
        };
      }

      const paymentEntry = {
        ...paymentData,
        ...payResponse,
        plan: subscription.plan,
        monthOfSubscription: subscription.months,
        createdAt: new Date(),
      };

      const updateData = {
        payment: arrayUnion(paymentEntry),
        activeSubscription: activeSubscription_,
      };

      if (Array.isArray(referralList)) {
        updateData.referrals = referralList;
      } else {
        updateData.referrals = [];
      }

      await updateDoc(ref, updateData);

      return {
        payment: [paymentEntry],
        referrals: referralList,
        activeSubscription: activeSubscription_,
      };
    } catch (e) {
      console.error("Error in renewSubscription:", e);
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const forgotPassUser = createAsyncThunk(
  "user/forgotPassUser",
  async ({ formdata }, thunkAPI) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, formdata.email);

      return {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (
    { formdata, uid, email, profile_, logo_, qr_, signature_ },
    thunkAPI
  ) => {
    try {
      let logo, profile, qr, signature;

      if (email !== formdata.email) {
        const auth = getAuth();
        await updateEmail(auth.currentUser, formdata.email);
      }
      if (formdata?.profile_location) {
        profile = await uploadFile(formdata.profile, "profile");
      }

      if (formdata?.logo_location) {
        logo = await uploadFile(formdata.logo, "logo");
      }

      if (formdata?.qr_location) {
        qr = await uploadFile(formdata.qr, "qr");
      }

      if (formdata?.signature_location) {
        signature = await uploadFile(formdata.signature, "signature");
      }

      const profileRef = doc(firestore, "profile", uid);

      let data = {
        name: formdata.name,
        email: formdata.email,
        phoneNumber: formdata.phoneNumber,
        address: formdata.address,
        info: formdata.info,
        fb: formdata.fb,
        instagram: formdata.instagram,
        twitter: formdata.twitter,
      };

      if (logo) {
        await updateDoc(profileRef, {
          ...data,
          logo: logo,
        });
      } else {
        await updateDoc(profileRef, {
          ...data,
        });
      }

      if (profile) {
        await updateDoc(profileRef, {
          ...data,
          profile: profile,
        });
      } else {
        await updateDoc(profileRef, {
          ...data,
        });
      }

      if (qr) {
        await updateDoc(profileRef, {
          ...data,
          qr: qr,
        });
      } else {
        await updateDoc(profileRef, {
          ...data,
        });
      }

      if (signature) {
        await updateDoc(profileRef, {
          ...data,
          signature: signature,
        });
      } else {
        await updateDoc(profileRef, {
          ...data,
        });
      }

      return {
        ...formdata,
        logo: logo ?? logo_,
        profile: profile ?? profile_,
        qr: qr ?? qr_,
        signature: signature ?? signature_,
      };
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const deleteImage = createAsyncThunk(
  "user/deleteImage",
  async ({ uid, image }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      await updateDoc(profileRef, {
        [image]: "",
      });

      return { image };
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const userDetails = createAsyncThunk(
  "user/userDetails",
  async ({ uid }, thunkAPI) => {
    try {
      let data;

      const roleRef = doc(firestore, "profile", uid);
      const snapshot = await getDoc(roleRef);
      data = { ...snapshot.data() };

      //!DEBUG
      return { ...data };
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const getIntlPhoneNumber = createAsyncThunk(
  "user/getIntlPhoneNumber",
  async (_, thunkAPI) => {
    try {
      let data;

      const intlPhoneNumber = doc(
        firestore,
        "intlPhoneNumber",
        "intlPhoneNumber"
      );
      const snapshot = await getDoc(intlPhoneNumber);
      data = { ...snapshot.data() };

      //!DEBUG
      return { ...data };
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPasword",
  async ({ newPassword, oldPassword }, thunkAPI) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      const credential = EmailAuthProvider.credential(user.email, oldPassword);

      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      return {};
    } catch (e) {
      if (e.code === "auth/wrong-password") {
        e.message = "Existing password is wrong, retry!!";
      }

      if (e.code === "auth/too-many-requests") {
        e.message = "Something went wrong, retry after some time!!";
      }
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateRedeems = createAsyncThunk(
  "user/updateRedeems",
  async ({ referrals, uid }, thunkAPI) => {
    try {
      const roleRef = doc(firestore, "profile", uid);

      let refList = referrals.map((item, i) => {
        if (item.redeemed === false) {
          return {
            ...item,
            redeemed: true,
            redeemedDate: new Date(),
          };
        }
        return item;
      });

      await updateDoc(roleRef, {
        referrals: refList,
      });

      return { refList };
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const sendMessages = createAsyncThunk(
  "user/sendMessages",
  async ({ message, phoneNumber, name, email }, thunkAPI) => {
    try {
      let status, data;

      await fetch(
        // `http://localhost:4006/v1/fitify/contactusmail`,

        process.env.REACT_APP_WARRANTY + `/v1/warranty/contactusmail`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ message, phoneNumber, name, email }),
        }
      )
        .then((res) => {
          status = res.status;
          return res.json();
        })
        .then((res) => {
          data = res;
        })
        .catch((err) => {
          status = 500;
          data = err;
        });

      if (status === 200) {
        return { ...data };
      } else {
        return thunkAPI.rejectWithValue({ ...data });
      }
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateTermsAndConditions = createAsyncThunk(
  "user/updateTermsAndConditions",
  async ({ newTandC, uid }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      await updateDoc(profileRef, {
        termsAndConditions: newTandC,
      });
    } catch (error) {
      thunkAPI.rejectWithValue(error);
    }
  }
);

export const getTermsAndConditions = createAsyncThunk(
  "user/getTermsAndConditions",
  async ({ uid }, thunkAPI) => {
    try {
      let data;

      const roleRef = doc(firestore, "profile", uid);
      const snapshot = await getDoc(roleRef);
      data = { ...snapshot.data() };
      return data?.termsAndConditions;
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateDietChartSettings = createAsyncThunk(
  "user/updateDietChartSettings",
  async ({ newDietChartSettings, uid }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      await updateDoc(profileRef, {
        dietChartSettings: newDietChartSettings,
      });

      return newDietChartSettings;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Update failed");
    }
  }
);

export const updateDietChartConfiguration = createAsyncThunk(
  "user/updateDietChartConfiguration",
  async ({ newDietChartConfiguration, uid }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      await updateDoc(profileRef, {
        dietChartConfiguration: newDietChartConfiguration,
      });
    } catch (error) {
      thunkAPI.rejectWithValue(error);
    }
  }
);
export const getDietChartConfiguration = createAsyncThunk(
  "user/getDietChartConfiguration",
  async ({ uid }, thunkAPI) => {
    try {
      let data;

      const roleRef = doc(firestore, "profile", uid);
      const snapshot = await getDoc(roleRef);
      data = { ...snapshot.data() };
      return data?.dietChartConfiguration;
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const getDietChartSettings = createAsyncThunk(
  "user/getDietChartSettings",
  async ({ uid }, thunkAPI) => {
    try {
      let data;

      const roleRef = doc(firestore, "profile", uid);
      const snapshot = await getDoc(roleRef);

      data = { ...snapshot.data() };
      return data?.dietChartSettings;
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

// Utility to recursively remove undefined fields from an object or array
function removeUndefined(obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  } else if (obj instanceof Date) {
    return obj;
  } else if (obj && typeof obj === "object") {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = removeUndefined(value);
      }
      return acc;
    }, {});
  }
  return obj;
}

export const saveNewDietChartTemplate = createAsyncThunk(
  "user/saveNewDietChartTemplate",
  async (
    {
      templateName,
      dietPlan,
      chartType,
      weekPlan,
      note,
      uid,
      setSaveIsClicked,
    },
    thunkAPI
  ) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      const docSnap = await getDoc(profileRef);

      if (!docSnap.exists()) {
        return thunkAPI.rejectWithValue("Profile does not exist");
      }

      const dataArray = docSnap.data().dietChartTemplates || [];

      const existingTemplate = dataArray.find(
        (template) =>
          template.templateName.toLowerCase() === templateName.toLowerCase()
      );

      if (existingTemplate) {
        toast.error("Enter a different template name");
        return thunkAPI.rejectWithValue("Duplicate template name");
      }

      const newTemplate = {
        templateName,
        dietPlan,
        chartType,
        weekPlan,
        note,
        createdAt: new Date(),
      };

      const cleanedTemplate = removeUndefined(newTemplate);

      dataArray.push(cleanedTemplate);

      await updateDoc(profileRef, {
        dietChartTemplates: dataArray,
      });

      return dataArray;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editDietChartTemplate = createAsyncThunk(
  "user/editDietChartTemplate",
  async (
    {
      oldTemplateName, // existing template name to identify the template
      newTemplateName,
      dietPlan,
      chartType,
      weekPlan,
      note,
      uid,
    },
    thunkAPI
  ) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      const docSnap = await getDoc(profileRef);

      if (!docSnap.exists()) {
        return thunkAPI.rejectWithValue("Profile does not exist");
      }

      const dataArray = docSnap.data().dietChartTemplates || [];

      // Find the template to edit
      const templateIndex = dataArray.findIndex(
        (template) =>
          template.templateName.toLowerCase() === oldTemplateName.toLowerCase()
      );

      if (templateIndex === -1) {
        return thunkAPI.rejectWithValue("Template not found");
      }

      // Check for duplicate name (excluding the current template)
      const isSameNameExist = dataArray.some(
        (template, index) =>
          template.templateName.toLowerCase() ===
            newTemplateName.toLowerCase() && index !== templateIndex
      );
      if (isSameNameExist) {
        toast.error("Enter a different template name");
        return thunkAPI.rejectWithValue("Duplicate template name");
      }

      const cleanedTemplate = removeUndefined({
        ...dataArray[templateIndex],
        templateName: newTemplateName,
        dietPlan,
        chartType,
        weekPlan,
        note,
        updatedAt: new Date(),
      });

      dataArray[templateIndex] = cleanedTemplate;

      await updateDoc(profileRef, {
        dietChartTemplates: dataArray.map(removeUndefined),
      });

      return dataArray;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteDietChartTemplate = createAsyncThunk(
  "user/deleteDietChartTemplate",
  async ({ templateName, uid }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      const docSnap = await getDoc(profileRef);

      if (docSnap.exists()) {
        const dataArray = docSnap.data().dietChartTemplates || [];

        const isTemplateExist = dataArray.some(
          (template) =>
            template.templateName.toLowerCase() === templateName.toLowerCase()
        );
        if (!isTemplateExist) {
          toast.error("Template not found");
          return thunkAPI.rejectWithValue("Template not found");
        }

        const updatedArray = dataArray.filter(
          (template) =>
            template.templateName.toLowerCase() !== templateName.toLowerCase()
        );

        await updateDoc(profileRef, {
          dietChartTemplates: updatedArray,
        });

        return updatedArray;
      } else {
        return thunkAPI.rejectWithValue("Profile does not exist");
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getSavedDietChartTemplates = createAsyncThunk(
  "user/getSavedDietChartTemplates",
  async ({ uid }, thunkAPI) => {
    try {
      let data;

      const roleRef = doc(firestore, "profile", uid);
      const snapshot = await getDoc(roleRef);

      data = { ...snapshot.data() };
      return data?.dietChartTemplates;
    } catch (e) {
      thunkAPI.rejectWithValue(e);
    }
  }
);

export const saveGSTNumber = createAsyncThunk(
  "user/saveGSTNumber",
  async ({ uid, gstNumber, setSaveIsClicked }, thunkAPI) => {
    try {
      const profileRef = doc(firestore, "profile", uid);
      const docSnap = await getDoc(profileRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        await updateDoc(profileRef, {
          ...data,
          gstNumber: gstNumber,
        });

        const serviceQuery = query(
          collection(firestore, "service"),
          where("adminId", "==", uid)
        );
        const serviceSnapshot = await getDocs(serviceQuery);
        const gstEnabledServices = serviceSnapshot.docs.filter((serviceDoc) => {
          const serviceData = serviceDoc.data();
          return serviceData?.checkGst || serviceData?.gstNumber;
        });

        for (let i = 0; i < gstEnabledServices.length; i += 500) {
          const batch = writeBatch(firestore);
          gstEnabledServices.slice(i, i + 500).forEach((serviceDoc) => {
            batch.update(serviceDoc.ref, {
              gstNumber,
              updatedAt: new Date(),
            });
          });
          await batch.commit();
        }

        setSaveIsClicked(false);
        return { ...data, gstNumber };
      }
    } catch (error) {
      setSaveIsClicked(false);
      thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateFcmToken = createAsyncThunk(
  "user/updateFcmToken",
  async ({ fcmToken }, thunkAPI) => {
    try {
      return { fcmToken };
    } catch (error) {
      thunkAPI.rejectWithValue(error);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState: {
    auth: false, // authentication
    uid: null,
    createdUserId: null,
    token: null,
    isSuperAdmin: false,

    name: null,
    email: "",
    phoneNumber: null,
    address: null,
    info: null,
    fb: null,
    instagram: null,
    twitter: null,

    profile: null,
    logo: null,
    qr: null,
    signature: null,
    code: null,
    referredBy: null,
    referrals: null,
    activeSubscription: null,
    payment: [],
    termsAndConditions: termsAndConditionDefault,
    dietChartSettings: null,
    dietChartTemplates: null,

    isFetching: false,
    isSuccess: false,
    isError: false,
    message: null,
    redirectToPayment: false,
    accessModules: null,
    gstNumber: null,
    unitList: null,
    gstTreatmentsList: null,
    fcmToken: null,
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.message = null;
      return state;
    },
    setCreatedUserId: (state, action) => {
      state.createdUserId = action.payload;
    },
    resetRedirectToPayment: (state) => {
      state.redirectToPayment = false;
    },
    logout: (state) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.auth = false;
      state.name = null;
      state.phoneNumber = null;
      state.email = null;
      state.address = null;
      state.info = null;
      state.fb = null;
      state.instagram = null;
      state.twitter = null;
      state.activeSubscription = null;
      state.profile = null;
      state.logo = null;
      state.qr = null;
      state.signature = null;
      state.code = null;
      state.referredBy = null;
      state.payment = null;

      state.uid = null;
      state.token = null;
      state.inventory = null;
      state.referrals = null;
      state.accessModules = null;
      state.redirectToPayment = false;
      return state;
    },
  },
  extraReducers: {
    [loginUser.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.auth = true;
      state.uid = payload?.uid;
      state.name = payload?.name;
      state.email = payload?.email;
      state.phoneNumber = payload?.phoneNumber;
      state.address = payload?.address;
      state.info = payload?.info;
      state.fb = payload?.fb;
      state.instagram = payload?.instagram;
      state.twitter = payload?.twitter;
      state.activeSubscription = payload?.activeSubscription;
      state.profile = payload?.profile;
      state.logo = payload?.logo;
      state.qr = payload?.qr;
      state.signature = payload?.signature;
      state.payment = payload?.payment;
      state.code = payload.code;
      state.referredBy = payload.referredBy;
      state.token = payload?.accessToken;
      state.referrals = payload?.referrals;
      state.accessModules = payload?.accessModules;
      state.isEmployee = payload?.isEmployee;
      state.gstNumber = payload?.gstNumber;
      state.hsnCode = payload?.hsnCode;
      state.unitList = payload?.unitList;
      state.gstTreatmentsList = payload?.gstTreatmentsList;
      state.redirectToPayment = false;
      state.isSuperAdmin = payload.isSuperAdmin;
      state.dietChartSettings = payload?.dietChartSettings;
      return state;
    },
    [loginUser.pending]: (state) => {
      state.isFetching = true;
    },
    [loginUser.rejected]: (state, { payload }) => {
      state.isSuccess = false;
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Credentials are invalid, retry";
      state.redirectToPayment = payload?.redirectToPayment;
      state.uid = payload?.uid;
      state.referrals = payload?.referrals;
      state.name = payload?.name;
      state.email = payload?.email;
      state.phoneNumber = payload?.phoneNumber;
      state.address = payload?.address;
      state.info = payload?.info;
    },

    [sendMessages.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Message send successfully";
      return state;
    },
    [sendMessages.pending]: (state) => {
      state.isFetching = true;
    },
    [sendMessages.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "SERVER ERROR";
    },
    [updateFcmToken.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.fcmToken = payload?.fcmToken;
      return state;
    },
    [updateFcmToken.pending]: (state) => {
      state.isFetching = true;
    },
    [updateFcmToken.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "SERVER ERROR";
    },

    [registerUser.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = payload.message;
      state.createdUserId = payload.userId;
      return state;
    },
    [registerUser.pending]: (state) => {
      state.isFetching = true;
    },
    [registerUser.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [renewSubscription.fulfilled]: (state, { payload }) => {
      state.payment = [...(state.payment || []), ...(payload.payment || [])];
      state.activeSubscription = payload.activeSubscription;
      state.referrals = payload.referrals;
      state.redirectToPayment = false;
      state.error = false;
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Subscription renewed successfully";
      return state;
    },
    [renewSubscription.pending]: (state) => {
      state.isFetching = true;
    },
    [renewSubscription.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [updateRedeems.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.referrals = payload?.refList;
      state.message = "Redeemeded successfully ";
      return state;
    },

    [updateRedeems.pending]: (state) => {
      state.isFetching = true;
    },

    [updateRedeems.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [forgotPassUser.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message =
        "Email has been sent to your e-mail a/c, plz check for resetting password!!";
      return state;
    },

    [forgotPassUser.pending]: (state) => {
      state.isFetching = true;
    },

    [forgotPassUser.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [updateProfile.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;

      state.name = payload?.name;
      state.email = payload?.email;
      state.phoneNumber = payload?.phoneNumber;
      state.address = payload.address;
      state.info = payload.info;
      state.fb = payload.fb;
      state.instagram = payload.instagram;
      state.twitter = payload.twitter;
      state.logo = payload.logo;
      state.qr = payload.qr;
      state.profile = payload.profile;
      state.qr = payload.qr;
      state.signature = payload.signature;
      state.message = "Profile updated successfully!!";
      return state;
    },

    [updateProfile.pending]: (state) => {
      state.isFetching = true;
    },

    [updateProfile.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [resetPassword.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Password updated successfully";
      return state;
    },
    [resetPassword.pending]: (state) => {
      state.isFetching = true;
    },
    [resetPassword.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [deleteImage.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state[payload.image] = "";
      state.message = `${payload.image} image deleted successfully`;
      return state;
    },

    [deleteImage.pending]: (state) => {
      state.isFetching = true;
    },

    [deleteImage.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [getIntlPhoneNumber.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.intlPhoneNumber = payload;
      return state;
    },

    [getIntlPhoneNumber.pending]: (state) => {
      state.isFetching = true;
    },

    [getIntlPhoneNumber.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [userDetails.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.name = payload?.name;
      state.email = payload?.email;
      state.phoneNumber = payload?.phoneNumber;
      state.address = payload?.address;
      state.info = payload?.info;
      state.fb = payload?.fb;
      state.qr = payload?.qr;
      state.instagram = payload?.instagram;
      state.twitter = payload?.twitter;

      state.profile = payload?.profile;
      state.logo = payload?.logo;
      state.signature = payload?.signature;
      state.payment = payload?.payment;
      state.code = payload.code;
      state.referredBy = payload.referredBy;
      state.token = payload?.accessToken;
      state.referrals = payload?.referrals;
      return state;
    },
    [userDetails.pending]: (state) => {
      state.isFetching = true;
    },
    [userDetails.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [updateTermsAndConditions.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "T & C changed successfully ";
      return state;
    },

    [updateTermsAndConditions.pending]: (state) => {
      state.isFetching = true;
    },

    [updateTermsAndConditions.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [getTermsAndConditions.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.termsAndConditions = payload;
      return state;
    },

    [getTermsAndConditions.pending]: (state) => {
      state.isFetching = true;
    },

    [getTermsAndConditions.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [updateDietChartSettings.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Diet chart settings saved successfully";
      state.dietChartSettings = payload;
    },

    [updateDietChartSettings.pending]: (state) => {
      state.isFetching = true;
    },

    [updateDietChartSettings.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [updateDietChartConfiguration.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.message = "Diet chart Configuration saved successfully ";
      return state;
    },

    [updateDietChartConfiguration.pending]: (state) => {
      state.isFetching = true;
    },

    [updateDietChartConfiguration.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [getDietChartSettings.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.dietChartSettings = payload;
      return state;
    },

    [getDietChartSettings.pending]: (state) => {
      state.isFetching = true;
    },

    [getDietChartSettings.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [getDietChartConfiguration.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.dietChartConfiguration = payload;
      return state;
    },

    [getDietChartConfiguration.pending]: (state) => {
      state.isFetching = true;
    },

    [getDietChartConfiguration.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [saveNewDietChartTemplate.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.isSuccess = true;
        state.isFetching = false;
        state.message = "Diet chart template saved successfully ";
        return state;
      } else {
        state.isSuccess = false;
        state.isFetching = false;
        state.message = "No data returned while saving template.";
        toast.error("Saving failed. Try again.");
      }
    },

    [saveNewDietChartTemplate.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },

    [saveNewDietChartTemplate.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
      state.isSuccess = false;
    },

    [editDietChartTemplate.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },

    [editDietChartTemplate.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.isSuccess = true;
        state.isFetching = false;
        state.message = "Diet chart template updated successfully";
        return state;
      } else {
        state.isSuccess = false;
        state.isFetching = false;
        state.message = "No data returned while updating template.";
        toast.error("Update failed. Try again.");
      }
    },

    [editDietChartTemplate.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
      state.isSuccess = false;
    },

    [deleteDietChartTemplate.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.isSuccess = true;
        state.isFetching = false;
        state.message = "Diet chart template deleted successfully";
        state.dietChartTemplates = payload;
        return state;
      } else {
        state.isSuccess = false;
        state.isFetching = false;
        state.message = "No data returned while deleting template.";
        toast.error("Delete failed. Try again.");
      }
    },

    [deleteDietChartTemplate.pending]: (state) => {
      state.isFetching = true;
    },

    [deleteDietChartTemplate.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },

    [saveGSTNumber.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.gstNumber = payload.gstNumber;
        state.isSuccess = true;
        state.isFetching = false;
        state.message = "GST number saved successfully!";
        return state;
      }
    },

    [saveGSTNumber.pending]: (state) => {
      state.isFetching = true;
    },

    [saveGSTNumber.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
    },
    [getSavedDietChartTemplates.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.isFetching = false;
      state.dietChartTemplates = payload;
      return state;
    },

    [getSavedDietChartTemplates.pending]: (state) => {
      state.isFetching = true;
    },

    [getSavedDietChartTemplates.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.message = payload?.message || "Something went wrong, retry";
      state.isSuccess = false;
    },
  },
});

export const { clearState, logout, setCreatedUserId, resetRedirectToPayment } =
  userSlice.actions;

export const userSelector = (state) => state.user;
